// ABOUTME: Card with circular gauge for displaying workspace usage vs limits.
// ABOUTME: Color-coded ring showing percentage with formatted numbers and label.

import { Card } from '@/components/ui/Card';

interface UsageGaugeCardProps {
  label: string;
  used: number;
  limit: number; // -1 means unlimited
}

export function UsageGaugeCard({ label, used, limit }: UsageGaugeCardProps) {
  const isUnlimited = limit === -1;

  // Calculate percentage
  const percentage = !isUnlimited && limit > 0 ? Math.min((used / limit) * 100, 100) : 0;

  // Determine color based on utilization
  let ringColor: string;
  let textColor: string;
  if (isUnlimited) {
    ringColor = '#d6d3d1'; // surface-300
    textColor = '#78716c'; // surface-500
  } else if (percentage >= 90) {
    ringColor = '#ef4444'; // error
    textColor = '#ef4444';
  } else if (percentage >= 75) {
    ringColor = '#f59e0b'; // warning
    textColor = '#f59e0b';
  } else {
    ringColor = '#22c55e'; // success
    textColor = '#22c55e';
  }

  // Format numbers with commas
  const formatNumber = (num: number) => num.toLocaleString();

  // SVG circle parameters
  const size = 80;
  const strokeWidth = 6;
  const center = size / 2;
  const radius = center - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <Card variant="surface" className="flex flex-col items-center justify-center py-6">
      {/* Circular gauge */}
      <div className="relative w-20 h-20 mb-3">
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e7e5e4"
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          {!isUnlimited && (
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={ringColor}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className="transition-all duration-500"
            />
          )}
        </svg>
        {/* Percentage text in center */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-semibold" style={{ color: textColor }}>
            {isUnlimited ? '∞' : `${percentage.toFixed(1)}%`}
          </span>
        </div>
      </div>

      {/* Numbers */}
      <div className="text-sm font-mono text-surface-600 mb-1">
        {isUnlimited ? (
          <span>{formatNumber(used)} / Unlimited</span>
        ) : (
          <span>
            {formatNumber(used)} / {formatNumber(limit)}
          </span>
        )}
      </div>

      {/* Label */}
      <div className="text-xs text-surface-500 text-center">{label}</div>
    </Card>
  );
}
